import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ClipboardCheck,
  Send,
  FileWarning,
  ShieldCheck,
  Clock,
  ArrowRight,
  FolderKanban,
  Flame,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatCard } from '@/components/shared/StatCard';
import { UrgencyBadge } from '@/components/shared/UrgencyBadge';
import { LiveRegulatoryFeed } from '@/components/dashboard/LiveRegulatoryFeed';
import { DepartmentStatusPanel } from '@/components/dashboard/DepartmentStatusPanel';
import { ChangeEventDetailPanel } from '@/components/dashboard/ChangeEventDetailPanel';
import { PanelErrorBoundary } from '@/components/dashboard/PanelErrorBoundary';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import type { ChangeEventSummary } from '@/types/api';
import { cn } from '@/lib/utils';

function calcHealthScore(overdue: number, pending: number, active: number): number {
  return Math.max(0, Math.min(100, 100 - (overdue * 15) - (pending * 8) - Math.max(0, active - 3) * 3));
}

function getHealthConfig(score: number) {
  if (score >= 80) return { label: 'Strong', color: 'text-compliance-low', bg: 'bg-compliance-low' };
  if (score >= 60) return { label: 'Needs Attention', color: 'text-compliance-medium', bg: 'bg-compliance-medium' };
  if (score >= 40) return { label: 'At Risk', color: 'text-compliance-high', bg: 'bg-compliance-high' };
  return { label: 'Critical', color: 'text-compliance-critical', bg: 'bg-compliance-critical' };
}

function formatDepartment(dept: string): string {
  return dept
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const handleCloseDetail = useCallback(() => setSelectedEventId(null), []);

  const { data: changeEvents } = useQuery({
    queryKey: ['changeEvents', 'recent'],
    queryFn: () => api.changeEvents.list({ limit: 0 }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const { data: changeEventsTop } = useQuery({
    queryKey: ['changeEvents', 'top5'],
    queryFn: () => api.changeEvents.list({ limit: 5 }) as unknown as Promise<{ events: ChangeEventSummary[]; total: number }>,
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const { data: reviewSessions } = useQuery({
    queryKey: ['reviewSessions', 'pending'],
    queryFn: () => api.reviewSessions.list({ status: 'pending', limit: 0 }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const { data: publishQueue } = useQuery({
    queryKey: ['publishQueue', 'ready'],
    queryFn: () => api.publishQueue.list({ status: 'queued', limit: 0 }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  const { data: attestations } = useQuery({
    queryKey: ['attestations', 'overdue'],
    queryFn: () => api.attestations.list({ status: 'overdue', limit: 0 }),
    enabled: !!user,
    refetchInterval: 30_000,
  });

  // ── Workflow Engine Queries ──────────────────────────────
  const { data: workflowDashboard } = useQuery({
    queryKey: ['workflow-dashboard'],
    queryFn: () => api.dashboard.overview(),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const { data: projectSummary } = useQuery({
    queryKey: ['workflow-project-summary'],
    queryFn: () => api.projects.summary(),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const { data: recentProjects } = useQuery({
    queryKey: ['workflow-recent-projects'],
    queryFn: () => api.projects.list({ limit: 5, status: 'in_progress' }),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const overdueCount = attestations?.total ?? 0;
  const pendingCount = reviewSessions?.total ?? 0;
  const activeCount = changeEvents?.total ?? 0;
  const healthScore = calcHealthScore(overdueCount, pendingCount, activeCount);
  const healthConfig = getHealthConfig(healthScore);

  // Build "Needs Attention" items — top 3 by deadline proximity and urgency
  const attentionItems = (changeEventsTop?.events ?? [])
    .filter((ev) => ev.effectiveDate)
    .map((ev) => {
      const daysLeft = Math.ceil((new Date(ev.effectiveDate!).getTime() - Date.now()) / 86_400_000);
      const urgency = Number(ev.urgencyScore) || 0;
      return { ...ev, daysLeft, urgency };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft || b.urgency - a.urgency)
    .slice(0, 3);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-6">
      <PageHeader
        title="Dashboard"
        subtitle="Real-time regulatory intelligence and compliance overview"
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Active Changes"
          value={changeEvents?.total ?? '—'}
          icon={AlertTriangle}
          colorOverride="text-compliance-high"
          trend="up"
          delta="+3 vs last week"
          onClick={() => navigate('/feed')}
        />
        <StatCard
          label="Pending Reviews"
          value={reviewSessions?.total ?? '—'}
          icon={ClipboardCheck}
          colorOverride="text-compliance-medium"
          trend="up"
          trendColor="text-compliance-high"
          delta="+2 vs last week"
          onClick={() => navigate('/review')}
        />
        <StatCard
          label="Ready to Publish"
          value={publishQueue?.total ?? '—'}
          icon={Send}
          colorOverride="text-teal"
          trend="neutral"
          delta="no change"
          onClick={() => navigate('/publish')}
        />
        <StatCard
          label="Overdue Attestations"
          value={attestations?.total ?? '—'}
          icon={FileWarning}
          colorOverride="text-compliance-critical"
          trend="down"
          trendColor="text-compliance-low"
          delta="-1 vs last week"
          onClick={() => navigate('/attestations')}
        />
      </div>

      {/* Workflow Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Active Projects"
          value={(projectSummary?.open ?? 0) + (projectSummary?.inProgress ?? 0)}
          icon={FolderKanban}
          colorOverride="text-teal"
          onClick={() => navigate('/projects')}
        />
        <StatCard
          label="Overdue Tasks"
          value={workflowDashboard?.overdueTaskCount ?? 0}
          icon={Clock}
          colorOverride={
            (workflowDashboard?.overdueTaskCount ?? 0) > 0
              ? 'text-compliance-critical'
              : 'text-muted-foreground'
          }
        />
        <StatCard
          label="Open Escalations"
          value={workflowDashboard?.openEscalations ?? 0}
          icon={Flame}
          colorOverride={
            (workflowDashboard?.openEscalations ?? 0) > 0
              ? 'text-compliance-high'
              : 'text-muted-foreground'
          }
        />
        <StatCard
          label="Completed Projects"
          value={projectSummary?.completed ?? 0}
          icon={CheckCircle2}
          colorOverride="text-compliance-low"
          onClick={() => navigate('/projects')}
        />
      </div>

      {/* Health Score + Needs Attention */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Compliance Health Score */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className={cn('h-5 w-5', healthConfig.color)} />
              <h3 className="text-sm font-semibold text-foreground">Compliance Health</h3>
            </div>
            <div className="flex items-end gap-3">
              <span className={cn('text-4xl font-bold', healthConfig.color)}>{healthScore}</span>
              <span className={cn('mb-1 text-sm font-medium', healthConfig.color)}>{healthConfig.label}</span>
            </div>
            <div className="mt-3 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', healthConfig.bg)}
                style={{ width: `${healthScore}%` }}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {overdueCount} overdue · {pendingCount} pending · {activeCount} active
            </p>
          </CardContent>
        </Card>

        {/* Needs Your Attention */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-compliance-high" />
                <h3 className="text-sm font-semibold text-foreground">Needs Your Attention</h3>
              </div>
              <button
                className="flex items-center gap-1 text-xs text-teal hover:underline"
                onClick={() => navigate('/review')}
              >
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {attentionItems.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No urgent items right now.</p>
            ) : (
              <div className="space-y-2">
                {attentionItems.map((item) => (
                  <div
                    key={item.eventId}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2 cursor-pointer transition-colors hover:bg-muted/40"
                    onClick={() => setSelectedEventId(item.eventId)}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{item.sourceName}</p>
                      <p className="text-xs text-muted-foreground">{item.affectedDepartments.map(formatDepartment).join(', ')}</p>
                    </div>
                    <div className="ml-3 flex items-center gap-2 shrink-0">
                      <span className={cn(
                        'flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        item.daysLeft <= 14 ? 'bg-compliance-critical/15 text-compliance-critical'
                          : item.daysLeft <= 30 ? 'bg-compliance-high/15 text-compliance-high'
                          : 'bg-compliance-medium/15 text-compliance-medium',
                      )}>
                        <Clock className="h-2.5 w-2.5" />
                        {item.daysLeft < 0 ? `${Math.abs(item.daysLeft)}d overdue` : `${item.daysLeft}d left`}
                      </span>
                      <UrgencyBadge score={item.urgency} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compliance Projects + Department Progress */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Active Projects */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FolderKanban className="h-4 w-4 text-teal" />
                <h3 className="text-sm font-semibold text-foreground">Active Compliance Projects</h3>
              </div>
              <button
                className="flex items-center gap-1 text-xs text-teal hover:underline"
                onClick={() => navigate('/projects')}
              >
                View all <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            {(recentProjects?.projects ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No active projects.</p>
            ) : (
              <div className="space-y-3">
                {(recentProjects?.projects ?? []).map((p) => {
                  const isOverdue = p.deadline && new Date(p.deadline) < new Date() && p.status !== 'completed';
                  return (
                    <div
                      key={p.projectId}
                      className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5 cursor-pointer transition-colors hover:bg-muted/40"
                      onClick={() => navigate(`/projects/${p.projectId}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{p.title}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <Progress value={p.progressPct} className="h-1.5 flex-1" />
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {p.tasksCompleted}/{p.taskCount}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px]',
                            p.priority === 'critical' && 'border-compliance-critical text-compliance-critical',
                            p.priority === 'high' && 'border-compliance-high text-compliance-high',
                            p.priority === 'medium' && 'border-compliance-medium text-compliance-medium',
                            p.priority === 'low' && 'border-compliance-low text-compliance-low',
                          )}
                        >
                          {p.priority}
                        </Badge>
                        {p.deadline && (
                          <span className={cn('text-[10px]', isOverdue ? 'text-compliance-critical font-semibold' : 'text-muted-foreground')}>
                            {isOverdue ? 'Overdue' : new Date(p.deadline).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Department Task Progress */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <ClipboardCheck className="h-4 w-4 text-teal" />
              <h3 className="text-sm font-semibold text-foreground">Dept. Task Progress</h3>
            </div>
            {(workflowDashboard?.departmentProgress ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No department data.</p>
            ) : (
              <div className="space-y-3">
                {(workflowDashboard?.departmentProgress ?? []).map((dept) => (
                  <div key={dept.department}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-foreground">{formatDepartment(dept.department)}</span>
                      <span className="text-muted-foreground">
                        {dept.completedTasks}/{dept.totalTasks}
                        {dept.overdueTasks > 0 && (
                          <span className="ml-1 text-compliance-critical">({dept.overdueTasks} overdue)</span>
                        )}
                      </span>
                    </div>
                    <Progress value={dept.progressPct} className="mt-1 h-1.5" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Deadlines */}
      {(workflowDashboard?.upcomingDeadlines ?? []).length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-4 w-4 text-compliance-medium" />
              <h3 className="text-sm font-semibold text-foreground">Upcoming Project Deadlines</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {(workflowDashboard?.upcomingDeadlines ?? []).map((proj) => {
                const daysLeft = proj.deadline
                  ? Math.ceil((new Date(proj.deadline).getTime() - Date.now()) / 86_400_000)
                  : null;
                return (
                  <div
                    key={proj.projectId}
                    className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-xs cursor-pointer hover:bg-muted/40"
                    onClick={() => navigate(`/projects/${proj.projectId}`)}
                  >
                    <span className="font-medium text-foreground truncate max-w-[200px]">{proj.title}</span>
                    {daysLeft !== null && (
                      <span className={cn(
                        'shrink-0 rounded-full px-2 py-0.5 font-semibold',
                        daysLeft <= 3 ? 'bg-compliance-critical/15 text-compliance-critical'
                          : daysLeft <= 7 ? 'bg-compliance-high/15 text-compliance-high'
                          : 'bg-compliance-medium/15 text-compliance-medium',
                      )}>
                        {daysLeft <= 0 ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main content: Feed + Departments */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex h-full flex-col rounded-lg border border-border bg-card p-5">
            <div className="mb-4">
              <h2 className="text-base font-semibold text-foreground">
                Live Regulatory Feed
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Showing last 30 days &middot; Updates in real time
              </p>
            </div>
            <div className="min-h-0 flex-1">
              <LiveRegulatoryFeed
                selectedEventId={selectedEventId}
                onSelectEvent={setSelectedEventId}
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <DepartmentStatusPanel />
        </div>
      </div>

      <PanelErrorBoundary onReset={handleCloseDetail}>
        <ChangeEventDetailPanel
          eventId={selectedEventId}
          onClose={handleCloseDetail}
        />
      </PanelErrorBoundary>
    </div>
  );
}
